import { useTheme } from "@emotion/react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Box, Grid, Typography, Chip, Autocomplete, RadioGroup, FormControlLabel,
  CircularProgress, Switch, Select, MenuItem,
} from "@mui/material";
import {
  GetInvestorSetting,
  UpdateInvestorSetting,
  FetchDealFlowConfig,
} from "api/investors";
import { ToggleAutoShortlist } from "api/deal_flow";
import CustomSwitch from "ui-components/CustomSwitch";
import { useToast } from "context/ToastContext";
import React, { useEffect, useRef, useState, useMemo } from "react";
import PerfectScrollbar from "react-perfect-scrollbar";
import { useSelector } from "react-redux";
import CustomRichTextEditor from "ui-components/formik/CustomRichTextEditor";
import DealFlowSkeleton from './DealFlowSkeleton';
import DeleteConfirmationDialog from "ui-components/popups/DeleteConfirmationPopup";
import { Form, FormikProvider, useFormik } from "formik";
import CustomInputField from "ui-components/formik/CustomInputField";
import CustomRadio from "ui-components/CustomRadio";
import CustomValidationText from "ui-components/CustomValidationText";
import CustomLabel from "ui-components/CustomLabel";
import GeoMultiSelect from "ui-components/GeoMultiSelect";
import Quill from "quill";
import { countRichTextCharacters } from "utilities/richText";
import ChipBlot from '../../common-components/ChipBlot';
import Cookies from "js-cookie";
import { FetchPipelinesStatuses } from 'api/startup_applications';
import { FetchAllCategories, FetchAllStages, FetchAllSubCategories, FetchAllBusinessModel } from 'api/utils';
import { categoryIdsToNames } from 'utils';
import TextField from '@mui/material/TextField';
import { InfoOutlined as InfoOutlinedIcon } from '@mui/icons-material';
import Tooltip from '@mui/material/Tooltip';
import * as Yup from "yup"
import { Primary } from 'ui-components/Buttons';

// Quill ChipBlot registration
// Register Custom Chip Blot
// const Embed = Quill.import('blots/embed');

// class ChipBlot extends Embed {
//   static blotName = 'chip';
//   static tagName = 'span';
//   static className = 'ql-chip-badge';

//   static create(value) {
//     const node = super.create();
//     node.setAttribute('contenteditable', 'false');
//     node.setAttribute('data-chip-value', value);
//     node.setAttribute('spellcheck', 'false');

//     node.style.display = 'inline-flex';
//     node.style.alignItems = 'center';
//     node.style.padding = '2px 10px';
//     node.style.margin = '0 2px';
//     node.style.borderRadius = '12px';
//     node.style.backgroundColor = '#e3f2fd';
//     node.style.color = '#1565c0';
//     node.style.fontSize = '0.813rem';
//     node.style.fontWeight = '500';
//     node.style.cursor = 'default';
//     node.style.border = '1px solid #90caf9';
//     node.style.userSelect = 'none';
//     node.style.verticalAlign = 'middle';

//     node.textContent = value;

//     return node;
//   }

//   static value(node) {
//     return node.getAttribute('data-chip-value');
//   }

//   length() {
//     return 1;
//   }
// }

Quill.register(ChipBlot);

// Helper function to strip HTML tags and get plain text
const getPlainText = (html) => {
  const temp = document.createElement('div');
  temp.innerHTML = html || '';
  return (temp.textContent || temp.innerText || '').trim();
};

const validationSchema = Yup.object({
  to: Yup.string().required("Recipient type is required"),
  mail_type: Yup.string().required(),
  // "No Email" hides both editors, and their error text renders inside those same hidden
  // blocks - so requiring them in that mode made Save fail with nothing on screen to say
  // why. Only enforced for the modes that actually show the fields.
  subject: Yup.string().when('mail_type', {
    is: (mailType) => mailType !== 'noemail',
    then: (schema) =>
      schema
        .required("Subject is required")
        .test('min-length', 'Subject must be at least 5 characters', function (value) {
          const plainText = getPlainText(value);
          return plainText.length >= 5;
        }),
    otherwise: (schema) => schema,
  }),
  message: Yup.string().when('mail_type', {
    is: (mailType) => mailType !== 'noemail',
    then: (schema) =>
      schema
        .required("Message body is required")
        .test('min-length', 'Message must be at least 5 characters', function (value) {
          const plainText = getPlainText(value);
          return plainText.length >= 5;
        }),
    otherwise: (schema) => schema,
  }),
});

// Settings renders this component once per tab: the "Investment Thesis" tab passes
// section="thesis" (thesis card only), the "Deal Flow" tab passes section="deal-flow"
// (Email Template + Table Customization). All state/hooks stay shared and untouched -
// only which card renders changes. Defaults to "deal-flow" for any existing caller.
const DealFlow = ({ section = "deal-flow" }) => {
  const showThesis = section === "thesis";
  const showDealFlow = section === "deal-flow";
  const theme = useTheme();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  let [investor_id, setInvestorID] = React.useState(Cookies.get("X-INV-ID") || null);

  const { data: dealFlowConfig, isLoading: isLoadingDealFlowConfig } = FetchDealFlowConfig();
  const { data: fetchedSettingFromBackend, isLoading: isFetching } = GetInvestorSetting({
    investor_id,
    module: "deal_flow",
  });
  const { mutateAsync: updateSettingAsync, isLoading: isUpdating } = UpdateInvestorSetting();

  // ----------- Local shadow for fetchedSetting --------------
  const [localFetchedSetting, setLocalFetchedSetting] = useState(null);

  // Sync backend only ONCE
  useEffect(() => {
    if (fetchedSettingFromBackend && !localFetchedSetting) {
      setLocalFetchedSetting(fetchedSettingFromBackend);
    }
  }, [fetchedSettingFromBackend, localFetchedSetting]);

  // UI table columns mapping
  const columns = useSelector(
    (state) => state?.investorDetails?.deal_flow_columns
  )
    ?.filter((item) => item.hidden !== false)
    ?.map((item) => ({
      label: item.Header,
      value: item.accessor,
    }));

  const editorRef = useRef(null);
  const subjectEditorRef = useRef(null);
  const [focusedEditor, setFocusedEditor] = useState("message");
  const [hasLoadedSettings, setHasLoadedSettings] = useState(false);

  // Auto-shortlist state
  const [autoShortlistEnabled, setAutoShortlistEnabled] = useState(false);
  const [autoShortlistDelayMs, setAutoShortlistDelayMs] = useState(0);
  const [autoShortlistConfirmDialog, setAutoShortlistConfirmDialog] = useState(false);
  const [pendingAutoShortlistState, setPendingAutoShortlistState] = useState(null);
  const { mutateAsync: toggleAutoShortlistAsync } = ToggleAutoShortlist();

  // Load auto-shortlist settings from backend
  useEffect(() => {
    if (localFetchedSetting?.auto_shortlist) {
      setAutoShortlistEnabled(localFetchedSetting.auto_shortlist.enabled || false);
      setAutoShortlistDelayMs(localFetchedSetting.auto_shortlist.delay_ms || 0);
    }
  }, [localFetchedSetting]);

  // --------------- FORMIK ---------------
  const formik = useFormik({
    initialValues: {
      to: "in-progress",
      mail_type: "default",
      subject: "",
      message: "",
    },
    validationSchema,
    enableReinitialize: false,
    validateOnBlur: false,
    validateOnMount: false,
    validateOnChange: false,
    onSubmit: async (values, { setSubmitting }) => {
      const payload = {
        [values?.to]: { ...values }
      };
      try {
        await updateSettingAsync(
          {
            investor_id,
            module: "deal_flow",
            key: "customize_email",
            payload,
          },
          {
            onSuccess: () => {
              // Update localFetchedSetting for immediate UX
              setLocalFetchedSetting((prev) => ({
                ...prev,
                customize_email: {
                  ...(prev?.customize_email || {}),
                  [values?.to]: { ...values }
                }
              }));
              // Invalidate Deal Flow settings so the listing refetches fresh data
              queryClient.invalidateQueries({ queryKey: ['GetInvestorSetting'] });
              addToast("Email template saved", "success");
            },
            onError: () => {
              addToast("Error saving email template", "error");
            },
          }
        );
      } finally {
        setSubmitting(false);
      }
    }
  });

  const formikForm = useFormik({
    initialValues: { hidden_columns: [] },
    enableReinitialize: false,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        await updateSettingAsync(
          {
            investor_id,
            module: "deal_flow",
            key: "table_customization",
            payload: values,
          },
          {
            onSuccess: () => {
              setLocalFetchedSetting((prev) => ({
                ...prev,
                table_customization: {
                  ...(prev?.table_customization || {}),
                  ...values
                }
              }));
              // The saved values become the new baseline, so Save is disabled until the next change.
              formikForm.resetForm({ values: { hidden_columns: values.hidden_columns } });
              // Invalidate Deal Flow settings so the listing refetches fresh data
              queryClient.invalidateQueries({ queryKey: ['GetInvestorSetting'] });
              addToast("Table customization saved", "success");
            },
            onError: () => {
              addToast("Error saving table customization", "error");
            },
          }
        );
      } finally {
        setSubmitting(false);
      }
    }
  });

  // Sync table columns ONCE
  useEffect(() => {
    if (localFetchedSetting && !hasLoadedSettings) {
      const hiddenColumns = localFetchedSetting?.table_customization?.hidden_columns || [];
      // resetForm sets the baseline too, so clearing every column back to none still counts as a change.
      formikForm.resetForm({ values: { hidden_columns: hiddenColumns } });
      setHasLoadedSettings(true);
    }
  }, [localFetchedSetting, hasLoadedSettings]);

  // --------------- INVESTMENT THESIS ---------------
  const [pipelineStatuses, setPipelineStatuses] = useState([]);
  const [hasLoadedThesis, setHasLoadedThesis] = useState(false);

  const { mutateAsync: fetchPipelineStatuses } = FetchPipelinesStatuses();
  const { data: categories = [] } = FetchAllCategories();
  const { data: stages = [] } = FetchAllStages();
  const { data: businessModels = [] } = FetchAllBusinessModel();

  const thesisFormik = useFormik({
    initialValues: {
      selected_status_id: '',
      rejected_status_id: '',
      stage: '',
      category: '',
      selectedCategories: '',
      sector: '',
      business_model: '',
      country: '',
      state: '',
      city: '',
      startup_criteria: '',
    },
    validateOnBlur: false,
    validateOnChange: true,
    enableReinitialize: false,
    validate: (values) => {
      const errors = {};
      if (!values.selected_status_id) errors.selected_status_id = 'Required';
      if (!values.rejected_status_id) errors.rejected_status_id = 'Required';
      if (!values.stage) errors.stage = 'Required';
      return errors;
    },
    onSubmit: async (values, { setSubmitting }) => {
      try {
        await updateSettingAsync(
          { investor_id, module: 'deal_flow', key: 'investment_thesis', payload: values },
          {
            onSuccess: () => {
              setLocalFetchedSetting((prev) => ({ ...prev, investment_thesis: { ...values } }));
              // Invalidate Deal Flow settings so the listing refetches fresh data
              queryClient.invalidateQueries({ queryKey: ['GetInvestorSetting'] });
              addToast('Investment thesis saved', 'success');
            },
            onError: () => addToast('Error saving investment thesis', 'error'),
          }
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  const { data: thesisSubCategories = [] } = FetchAllSubCategories({
    category: thesisFormik.values.selectedCategories?.split(','),
  });

  // Fetch pipeline statuses once
  useEffect(() => {
    if (investor_id && pipelineStatuses.length === 0) {
      fetchPipelineStatuses({ investor_id }, {
        onSuccess: (res) => {
          setPipelineStatuses(
            res?.data?.data?.map((item) => ({ label: item.status, value: item._id, mappedValue: item.value })) || []
          );
        },
      });
    }
  }, [investor_id]);

  // Pre-populate from saved setting ONCE (wait for categories so sector labels resolve)
  useEffect(() => {
    if (localFetchedSetting?.investment_thesis && !hasLoadedThesis && categories?.length) {
      const t = localFetchedSetting.investment_thesis;
      // selectedCategories (names) drives the sub-category/sector fetch. If it wasn't
      // saved (e.g. thesis seeded from onboarding), derive it from category ids.
      const selectedCategories = t.selectedCategories || categoryIdsToNames(t.category, categories);
      const values = {
        selected_status_id: t.selected_status_id || '',
        rejected_status_id: t.rejected_status_id || '',
        stage: t.stage || '',
        category: t.category || '',
        selectedCategories,
        sector: t.sector || '',
        business_model: t.business_model || '',
        country: t.country || '',
        state: t.state || '',
        city: t.city || '',
        startup_criteria: t.startup_criteria || '',
      };
      thesisFormik.setValues(values, false);
      Object.keys(values).forEach(field => {
        thesisFormik.setFieldTouched(field, false, false);
      });
      setHasLoadedThesis(true);
    }
  }, [localFetchedSetting, hasLoadedThesis, categories]);

  const ThesisInput = (params) => (
    <TextField
      {...params}
      variant="filled"
      InputProps={{
        ...params.InputProps,
        disableUnderline: true,
      }}
      sx={{
        width: '100%',
        marginBottom: 0,
        '& .MuiFilledInput-root': {
          backgroundColor: theme.palette.customColors.whiteBG,
          border: `1px solid ${theme.palette.customColors.g75}`,
          borderRadius: '8px',
          padding: '0.25rem 0.5rem !important',
          '&:hover': { backgroundColor: theme.palette.customColors.whiteBG },
          '&.Mui-focused': { backgroundColor: theme.palette.customColors.whiteBG, borderColor: theme.palette.customColors.g75 },
          // '& .MuiFilledInput-input': { padding: '13px 15px', height: 'auto', boxSizing: 'border-box' },
        },
        '& .MuiInputBase-input::placeholder': { color: theme.palette.customColors.g100, opacity: 1 },
      }}
    />
  );

  const thesisMultiSelectProps = (field, options, placeholder) => ({
    multiple: true,
    disablePortal: true,
    fullWidth: true,
    autoHighlight: true,
    options: options.filter((o) => !thesisFormik.values[field].split(',').filter(Boolean).includes(o.value)),
    value: thesisFormik.values[field].split(',').filter(Boolean).map((v) => ({
      label: options?.find((item) => item?.value === v)?.label || v,
      value: v,
    })),
    onChange: (_, newVal) => {
      thesisFormik.setFieldValue(field, newVal.map((o) => o.value).join(','));
      thesisFormik.setFieldTouched(field, true, false);
    },
    renderTags: (selected, getTagProps) =>
      selected.map((opt, i) => (
        <Chip key={opt.value} label={opt.label} {...getTagProps({ index: i })} />
      )),
    renderInput: (params) => <ThesisInput {...params} placeholder={placeholder} />,
  });

  // ---------- When "to" changes, set mail_type/subject/message from localFetchedSetting or defaults
  useEffect(() => {
    if (!dealFlowConfig?.deal_flow?.customized_email_template) return;
    const templates = dealFlowConfig.deal_flow.customized_email_template?.defaults;
    const toKey = formik.values.to || "in-progress";
    const defaultTemplate = templates[toKey];
    const saved = localFetchedSetting?.customize_email?.[toKey];
    // If we have saved mail_type, use it; otherwise use "default"
    const mailType = (saved && saved.mail_type) || "default";
    const subject =
      mailType === "default"
        ? defaultTemplate?.subject || ""
        : (saved && saved.subject?.trim()) || defaultTemplate?.subject || "";
    const message =
      mailType === "default"
        ? defaultTemplate?.message || ""
        : (saved && saved.message?.trim()) || defaultTemplate?.message || "";

    const values = { to: toKey, mail_type: mailType, subject, message };
    formik.setValues(values, false);
    ['to', 'mail_type', 'subject', 'message'].forEach(field => {
      formik.setFieldTouched(field, false, false);
    });
    setTimeout(() => {
      editorRef.current?.getEditor?.().clipboard.dangerouslyPasteHTML(message || "");
      subjectEditorRef.current?.getEditor?.().clipboard.dangerouslyPasteHTML(subject || "");
    }, 150);
  }, [formik.values.to, dealFlowConfig, localFetchedSetting]);

  // ---------- When mail_type changes, update subject/message, but never override mail_type
  useEffect(() => {
    if (!dealFlowConfig?.deal_flow?.customized_email_template) return;
    const templates = dealFlowConfig.deal_flow.customized_email_template?.defaults;
    const toKey = formik.values.to || "in-progress";
    const defaultTemplate = templates[toKey];
    const saved = localFetchedSetting?.customize_email?.[toKey];
    const subject =
      formik.values.mail_type === "default"
        ? defaultTemplate?.subject || ""
        : (saved && saved.subject?.trim()) || defaultTemplate?.subject || "";
    const message =
      formik.values.mail_type === "default"
        ? defaultTemplate?.message || ""
        : (saved && saved.message?.trim()) || defaultTemplate?.message || "";

    formik.setFieldValue("subject", subject, false);
    formik.setFieldValue("message", message, false);
    formik.setFieldTouched("subject", false, false);
    formik.setFieldTouched("message", false, false);
    setTimeout(() => {
      editorRef.current?.getEditor?.().clipboard.dangerouslyPasteHTML(message || "");
      subjectEditorRef.current?.getEditor?.().clipboard.dangerouslyPasteHTML(subject || "");
    }, 150);
  }, [formik.values.mail_type, formik.values.to, dealFlowConfig, localFetchedSetting]);

  // Chip Handler
  const handleChipClick = (chipText, targetEditor) => {
    const effectiveEditor = targetEditor || focusedEditor;
    const targetRef = effectiveEditor === "subject" ? subjectEditorRef : editorRef;
    const quill = targetRef.current?.getEditor?.();
    if (quill && typeof quill.insertEmbed === "function") {
      // The editor enforces its character limit on keydown, which insertEmbed never
      // triggers - so without this the chips walk straight past the limit and the
      // counter just goes negative. Counted the same way the visible counter counts:
      // the chip's label is what ends up in the text.
      const limits = dealFlowConfig?.deal_flow?.customized_email_template?.character_limit;
      const isSubject = effectiveEditor === "subject";
      const maxLength = isSubject ? (limits?.subject || 50) : (limits?.message || 2000);
      const used = countRichTextCharacters(formik.values[effectiveEditor] || "");
      // +1 for the space inserted after the chip below.
      if (used + chipText.length + 1 > maxLength) {
        addToast(
          `${isSubject ? "Subject" : "Message"} has no room left for "${chipText}". Remove some text first.`,
          "warning"
        );
        return;
      }
      const selection = quill.getSelection(true);
      const index = selection ? selection.index : quill.getLength() - 1;
      quill.insertEmbed(index, "chip", chipText, "user");
      quill.insertText(index + 1, " ", "user");
      quill.setSelection(index + 2, 0, "user");
      quill.focus();
    }
  };

  const mappedHideColumns = useMemo(() => {
    return (
      formikForm.values.hidden_columns
        ?.map((val) => columns?.find((c) => c.value === val))
        .filter(Boolean) || []
    );
  }, [formikForm.values.hidden_columns, columns]);

  const mappedSelectedColumns = useMemo(() => {
    return (
      columns?.filter((c) => !formikForm.values.hidden_columns?.includes(c.value)) || []
    );
  }, [formikForm.values.hidden_columns, columns]);

  if (isFetching || isLoadingDealFlowConfig || !localFetchedSetting) {
    return (
      // Same padding wrapper as the loaded return, so the card does not shift.
      <Box sx={{ px: { xs: 1, sm: 2, md: 2, lg: 3 }, maxWidth: "100%", overflow: "hidden" }}>
        <DealFlowSkeleton section={section} />
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 1, sm: 2, md: 2, lg: 3 }, maxWidth: "100%", overflow: "hidden" }}>
      <PerfectScrollbar
        component="div"
        style={{ display: "grid", maxHeight: "calc(100vh - 12rem)", gap: "1rem" }}
      >

        {/* Investment Thesis - its own Settings tab */}
        {showThesis && (
        <Box>
          <FormikProvider value={thesisFormik}>
            <Form onSubmit={thesisFormik.handleSubmit} style={{ maxWidth: '900px', marginInline: 'auto' }}>
              <Grid
                container xs={12} padding="1.25rem" gap="0.25rem"
                sx={{
                  border: `1px solid ${theme.palette.customColors.b50}`,
                  borderRadius: '8px',
                  background: theme.palette.customColors.whiteBG,
                  width: "-webkit-fill-available",
                  justifyContent: "center"
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mb: 1 }}>
                  <Typography variant="h3" fontWeight={600}>
                    Investment Thesis
                  </Typography>
                  {/* Label left, switch right - same as the Deal Flow list toggle. */}
                  <Tooltip
                    title="Shortlist Startups automatically as they enter your Dealflow based on your Investment Area"
                    placement="top"
                    arrow
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography
                        sx={{
                          cursor: 'help',
                          fontSize: '0.85rem',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          color: theme.palette.customColors.g500,
                        }}
                      >
                        Auto-Shortlist
                      </Typography>
                      <CustomSwitch
                        // Previews the pending state while the confirm dialog is open.
                        checked={autoShortlistConfirmDialog && pendingAutoShortlistState !== null
                          ? pendingAutoShortlistState
                          : autoShortlistEnabled}
                        onChange={(e) => {
                          const newEnabled = e.target.checked;
                          const hasRequiredFields =
                            localFetchedSetting?.investment_thesis?.selected_status_id &&
                            localFetchedSetting?.investment_thesis?.rejected_status_id &&
                            localFetchedSetting?.investment_thesis?.stage;

                          if (newEnabled && !hasRequiredFields) {
                            addToast('Complete your Investment Thesis first', 'warning');
                            return;
                          }

                          setPendingAutoShortlistState(newEnabled);
                          setAutoShortlistConfirmDialog(true);
                        }}
                        disabled={
                          !localFetchedSetting?.investment_thesis?.selected_status_id ||
                          !localFetchedSetting?.investment_thesis?.rejected_status_id ||
                          !localFetchedSetting?.investment_thesis?.stage
                        }
                        sx={{ ml: 1.5 }}
                      />
                    </Box>
                  </Tooltip>
                </Box>

                <Grid container spacing={1.5} sx={{ width: '100%' }}>
                  {/* Shortlisted Status */}
                  <Grid item xs={12} sm={6}>
                    <CustomLabel>Shortlisted Status <span style={{ color: '#DC2626' }}>*</span></CustomLabel>
                    <Autocomplete
                      fullWidth disablePortal autoHighlight
                      options={pipelineStatuses.filter((s) => {
                        const excluded = dealFlowConfig?.deal_flow?.investment_thesis?.excluded_selected_status_ids || [];
                        return s.mappedValue !== thesisFormik.values.rejected_status_id && !excluded.includes(s.mappedValue);
                      })}
                      value={pipelineStatuses.find((s) => s.value === thesisFormik.values.selected_status_id) || null}
                      onChange={(_, newVal) => {
                        thesisFormik.setFieldValue('selected_status_id', newVal?.value || '');
                        thesisFormik.setFieldTouched('selected_status_id', true, false);
                      }}
                      renderInput={(params) => <ThesisInput {...params} placeholder="Select status" />}
                    />
                    {thesisFormik.touched.selected_status_id && thesisFormik.errors.selected_status_id && (
                      <Typography sx={{ fontSize: '0.75rem', color: '#DC2626', mt: 0.5 }}>{thesisFormik.errors.selected_status_id}</Typography>
                    )}
                  </Grid>

                  {/* Rejected Status */}
                  <Grid item xs={12} sm={6}>
                    <CustomLabel>Rejected Status <span style={{ color: '#DC2626' }}>*</span></CustomLabel>
                    <Autocomplete
                      fullWidth disablePortal autoHighlight
                      options={pipelineStatuses.filter((s) => {
                        const excluded = dealFlowConfig?.deal_flow?.investment_thesis?.excluded_rejected_status_ids || [];
                        return s.mappedValue !== thesisFormik.values.selected_status_id && !excluded.includes(s.mappedValue);
                      })}
                      value={pipelineStatuses.find((s) => s.value === thesisFormik.values.rejected_status_id) || null}
                      onChange={(_, newVal) => {
                        thesisFormik.setFieldValue('rejected_status_id', newVal?.value || '');
                        thesisFormik.setFieldTouched('rejected_status_id', true, false);
                      }}
                      renderInput={(params) => <ThesisInput {...params} placeholder="Select status" />}
                    />
                    {thesisFormik.touched.rejected_status_id && thesisFormik.errors.rejected_status_id && (
                      <Typography sx={{ fontSize: '0.75rem', color: '#DC2626', mt: 0.5 }}>{thesisFormik.errors.rejected_status_id}</Typography>
                    )}
                  </Grid>

                  {/* Stage */}
                  <Grid item xs={12} sm={6}>
                    <CustomLabel>Stage <span style={{ color: '#DC2626' }}>*</span></CustomLabel>
                    <Autocomplete
                      {...thesisMultiSelectProps('stage', stages?.map((s) => ({ label: s.name, value: s?._id })) || [], 'Select stage')}
                    />
                    {thesisFormik.touched.stage && thesisFormik.errors.stage && (
                      <Typography sx={{ fontSize: '0.75rem', color: '#DC2626', mt: 0.5 }}>{thesisFormik.errors.stage}</Typography>
                    )}
                  </Grid>

                  {/* Category */}
                  <Grid item xs={12} sm={6}>
                    <CustomLabel>Category</CustomLabel>
                    <Autocomplete
                      {...thesisMultiSelectProps('category', categories?.map((cat) => ({ label: cat.name, value: cat?._id })) || [], 'Select category')}
                      onChange={(_, newVal) => {
                        thesisFormik.setFieldValue('selectedCategories', newVal.map((o) => o.label).join(','));
                        thesisFormik.setFieldValue('category', newVal.map((o) => o.value).join(','));
                        thesisFormik.setFieldValue('sector', '');
                      }}
                    />
                  </Grid>

                  {/* Sector — only if category selected */}
                  {thesisFormik.values.category && (
                    <Grid item xs={12} sm={6}>
                      <CustomLabel>Sector</CustomLabel>
                      <Autocomplete
                        {...thesisMultiSelectProps('sector', thesisSubCategories?.map((s) => ({ label: s.sub_category, value: s?._id })) || [], 'Select sector')}
                      />
                    </Grid>
                  )}

                  {/* Business Model */}
                  <Grid item xs={12} sm={thesisFormik.values.category ? 6 : 12}>
                    <CustomLabel>Business Model</CustomLabel>
                    <Autocomplete
                      {...thesisMultiSelectProps('business_model', businessModels?.map((s) => ({ label: s.name, value: s?._id })) || [], 'Select business model')}
                    />
                  </Grid>

                  {/* Geography */}
                  {/* <GeoMultiSelect
                    labels={{ country: 'Country', state: 'State', city: 'City' }}
                    countryIds={thesisFormik.values.country ? thesisFormik.values.country.split(',').filter(Boolean) : []}
                    stateIds={thesisFormik.values.state ? thesisFormik.values.state.split(',').filter(Boolean) : []}
                    cityIds={thesisFormik.values.city ? thesisFormik.values.city.split(',').filter(Boolean) : []}
                    onChange={({ countryIds, stateIds, cityIds }) => {
                      thesisFormik.setFieldValue('country', (countryIds || []).join(','));
                      thesisFormik.setFieldValue('state', (stateIds || []).join(','));
                      thesisFormik.setFieldValue('city', (cityIds || []).join(','));
                    }}
                  /> */}

                  {/* Startup Criteria */}
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <CustomLabel>Startup Criteria</CustomLabel>
                      <Tooltip title="Describe what makes an ideal startup for your portfolio" placement="top" arrow>
                        <InfoOutlinedIcon sx={{ fontSize: 16, color: theme.palette.customColors.g100, mb: '8px', cursor: 'help' }} />
                      </Tooltip>
                    </Box>
                    <TextField
                      fullWidth multiline rows={3} variant="filled"
                      placeholder="Example: High-growth SaaS with at least 2 co-founders..."
                      value={thesisFormik.values.startup_criteria}
                      onChange={(e) => thesisFormik.setFieldValue('startup_criteria', e.target.value)}
                      InputProps={{ disableUnderline: true }}
                      sx={{
                        marginBottom: 0,
                        '& .MuiFilledInput-root': {
                          backgroundColor: theme.palette.customColors.whiteBG,
                          border: `1px solid ${theme.palette.customColors.g75}`,
                          borderRadius: '8px',
                          padding: '0px',
                          '&:hover': { backgroundColor: theme.palette.customColors.whiteBG },
                          '&.Mui-focused': { backgroundColor: theme.palette.customColors.whiteBG, borderColor: theme.palette.customColors.g75 },
                          '& .MuiFilledInput-input': { padding: '13px 15px', boxSizing: 'border-box', fontSize: '0.875rem' },
                        },
                        '& .MuiInputBase-input::placeholder': { color: theme.palette.customColors.g100, opacity: 1 },
                      }}
                    />
                  </Grid>

                  {/* Save button */}
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' } }}>
                      <Primary
                        type="submit"
                        disabled={(thesisFormik.isSubmitting && isUpdating) || !thesisFormik.dirty}
                        sx={{ padding: '0.75rem 1.25rem', fontSize: '1rem', minWidth: '150px', marginTop: '1.5rem' }}
                      >
                        {(thesisFormik.isSubmitting && isUpdating) ? <CircularProgress sx={{ color: 'white', marginInline: 'auto' }} size={'1.75rem'} /> : 'Save'}
                      </Primary>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Form>
          </FormikProvider>

          {/* Same confirmation the Deal Flow list and board show. */}
          <DeleteConfirmationDialog
            open={autoShortlistConfirmDialog}
            handleClose={() => setAutoShortlistConfirmDialog(false)}
            handleSubmission={async () => {
              const nextEnabled = pendingAutoShortlistState;
              const previousEnabled = autoShortlistEnabled;
              // Optimistic: rolled back on failure.
              setAutoShortlistEnabled(nextEnabled);
              try {
                await toggleAutoShortlistAsync({
                  investor_id,
                  enabled: nextEnabled,
                  delay_ms: autoShortlistDelayMs
                });
                setLocalFetchedSetting((prev) => ({
                  ...prev,
                  auto_shortlist: {
                    enabled: nextEnabled,
                    delay_ms: autoShortlistDelayMs
                  }
                }));
                addToast(nextEnabled ? 'Auto-shortlist enabled' : 'Auto-shortlist disabled', 'success');
              } catch (err) {
                console.error('Auto-shortlist error:', err);
                setAutoShortlistEnabled(previousEnabled);
                addToast('Error updating auto-shortlist setting', 'error');
              }
              setAutoShortlistConfirmDialog(false);
            }}
            heading={pendingAutoShortlistState ? 'Enable Auto-Shortlist?' : 'Disable Auto-Shortlist?'}
            subheading={pendingAutoShortlistState
              ? 'When enabled, every new startup entering your deal flow will be automatically categorized as either Selected or Rejected based on your investment thesis.'
              : 'When disabled, new startups will no longer be automatically shortlisted.'}
            ActionText={pendingAutoShortlistState ? 'Enable' : 'Disable'}
            CancelText="Cancel"
            maxWidth="xs"
            sx={{
              "& > .MuiDialog-container > .MuiPaper-root": {
                maxWidth: 450,
                borderRadius: "20px"
              }
            }}
          />
        </Box>
        )}


        {/* Email Template Form */}
        {showDealFlow && (
        <Box>
          <FormikProvider value={formik}>
            <Form
              onSubmit={formik.handleSubmit}
              style={{ maxWidth: "900px", marginInline: "auto" }}
            >
              <Grid
                container xs={12} padding="1.25rem" gap="0.25rem"
                sx={{
                  border: `1px solid ${theme.palette.customColors.b50}`,
                  borderRadius: "8px", background: theme.palette.customColors.whiteBG,
                  width: "-webkit-fill-available",
                  justifyContent: "center"
                }}
              >
                <Typography variant="h3" fontWeight={600} sx={{ mb: 1, width: "100%" }}>
                  {dealFlowConfig?.deal_flow?.customized_email_template?.title ||
                    "Customize Email Template"}
                </Typography>
                <Grid item xs={12} sx={{}}>
                  <CustomLabel>To</CustomLabel>
                  <Autocomplete
                    options={
                      dealFlowConfig?.deal_flow?.customized_email_template?.to || []
                    }
                    value={
                      dealFlowConfig?.deal_flow?.customized_email_template?.to?.find(
                        (option) => option.value === formik.values.to
                      ) || null
                    }
                    onChange={(_, newValue) => {
                      formik.setFieldValue("to", newValue?.value || "in-progress");
                    }}
                    getOptionLabel={(option) => option?.label || ""}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    renderInput={(params) => (
                      <CustomInputField
                        {...params}
                        name="to"
                        sx={{
                          marginBottom: 0,
                          "& > .MuiFilledInput-root": {
                            borderRadius: "8px"
                          }
                        }}
                        // variant="outlined"
                        placeholder="Select recipient type"
                      />
                    )}
                    sx={{
                      "& .MuiAutocomplete-option.Mui-focused": {
                        backgroundColor: theme.palette.action.hover,
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} pt={1}>
                  <CustomLabel>Mail Type</CustomLabel>
                  <RadioGroup
                    sx={{ flexDirection: { xs: "column", md: "row" }, paddingTop: 0, flexWrap: "wrap", gap: "0.5rem" }}
                    name="mail_type"
                    value={formik.values.mail_type}
                    onChange={e => formik.setFieldValue("mail_type", e.target.value)}
                  >
                    {(dealFlowConfig?.deal_flow?.customized_email_template?.mail_types || []).map(
                      (mailType) => (
                        <FormControlLabel
                          key={mailType.value}
                          value={mailType.value}
                          control={<CustomRadio disableRipple />}
                          label={mailType.label}
                        />
                      )
                    )}
                  </RadioGroup>
                  {formik.errors.mail_type && (
                    <CustomValidationText
                      value={formik.errors.mail_type}
                      style={{ marginLeft: 0 }}
                    />
                  )}
                </Grid>

                {(formik.values.mail_type !== "noemail" && <Grid item xs={12} pt={1}>
                  <CustomLabel>Subject</CustomLabel>
                  <CustomRichTextEditor
                    ref={subjectEditorRef}
                    compact
                    name="subject"
                    placeholder={
                      dealFlowConfig?.deal_flow?.customized_email_template?.placeholders?.subject
                    }
                    value={formik.values.subject}
                    handleChange={(value) => formik.setFieldValue("subject", value)}
                    readOnly={formik.values.mail_type === "default"}
                    onFocus={() => setFocusedEditor("subject")}
                    maxLength={dealFlowConfig?.deal_flow?.customized_email_template?.character_limit?.subject || 50}
                    // toolbarOptions={[
                    //   // ['bold', 'italic', "underline"],
                    //   // No lists, images etc for subject
                    // ]}
                    toolbarOptions={null}
                    formats={[
                      "bold", "italic", "underline", "chip" // essential formats only
                    ]}
                    singleLine={true}
                  />
                  {formik.values.mail_type !== "default" && (
                    <Box sx={{ mt: 1, display: "flex", alignItems: "center", flexWrap: "wrap", gap: "0.5rem" }}>
                      <span>Add: </span>
                      {(dealFlowConfig?.deal_flow?.customized_email_template?.chips?.subject || []).map(
                        (chip) => (
                          <Chip
                            key={chip}
                            label={chip}
                            onClick={() => handleChipClick(chip, "subject")}
                            sx={{
                              cursor: "pointer",
                              "&:hover": { backgroundColor: theme.palette.primary.light },
                            }}
                          />
                        )
                      )}
                    </Box>
                  )}
                  {formik.errors.subject && (
                    <CustomValidationText value={formik.errors.subject} style={{ marginLeft: 0 }} />
                  )}
                </Grid>)}
                <Grid item xs={12} pt={1}>
                  {(formik.values.mail_type !== "noemail" && <>
                    <CustomLabel>Message</CustomLabel>
                    <CustomRichTextEditor
                      ref={editorRef}
                      name="message"
                      placeholder={
                        dealFlowConfig?.deal_flow?.customized_email_template?.placeholders?.message
                      }
                      value={formik.values.message}
                      handleChange={(value) => formik.setFieldValue("message", value)}
                      readOnly={formik.values.mail_type === "default"}
                      onFocus={() => setFocusedEditor("message")}
                      maxLength={dealFlowConfig?.deal_flow?.customized_email_template?.character_limit?.message || 2000}
                    // minHeight={"100px"}
                    />
                    {formik.values.mail_type !== "default" && (
                      <Box sx={{ mt: 1, display: "flex", alignItems: "center", flexWrap: "wrap", gap: "0.5rem" }}>
                        <span>Add: </span>
                        {(dealFlowConfig?.deal_flow?.customized_email_template?.chips?.message || []).map(
                          (chip) => (
                            <Chip
                              key={chip}
                              label={chip}
                              onClick={() => handleChipClick(chip, "message")}
                              sx={{
                                cursor: "pointer",
                                "&:hover": { backgroundColor: theme.palette.primary.light },
                              }}
                            />
                          )
                        )}
                      </Box>
                    )}
                    {formik.errors.message && (
                      <CustomValidationText value={formik.errors.message} style={{ marginLeft: 0 }} />
                    )}
                  </>)}
                  <Box sx={{
                    display: "flex",
                    justifyContent: { xs: "center", md: "flex-end" },
                  }}>
                    <Primary
                      type="submit"
                      disabled={(formik.isSubmitting && isUpdating) || !formik.dirty}
                      sx={{
                        padding: "0.75rem 1.25rem",
                        fontSize: "1rem",
                        minWidth: "150px",
                        marginTop: "1.5rem",
                      }}
                    >
                      {isUpdating && formik.isSubmitting ? <CircularProgress sx={{ color: "white", marginInline: 'auto' }} size={'1.75rem'} /> : "Save"}
                    </Primary>
                  </Box>
                </Grid>

              </Grid>
            </Form>
          </FormikProvider>
        </Box>
        )}
        {/* Table Customization Form */}
        {showDealFlow && (
        <Box>
          <FormikProvider value={formikForm}>
            <Form
              onSubmit={formikForm.handleSubmit}
              style={{ maxWidth: "900px", marginInline: "auto" }}
            >
              <Grid
                container xs={12} padding="1.25rem" gap="0.25rem"
                sx={{
                  border: `1px solid ${theme.palette.customColors.b50}`,
                  borderRadius: "8px",
                  background: theme.palette.customColors.whiteBG,
                  width: "-webkit-fill-available",
                  justifyContent: "center"
                }}
              >
                <Typography variant="h3" fontWeight={600} sx={{ mb: 1, width: "100%" }}>Table Customization</Typography>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight={500}>Hide Columns</Typography>
                  <Box sx={{ mt: "0.5rem" }}>
                    <Autocomplete
                      multiple
                      options={mappedSelectedColumns || []}
                      value={mappedHideColumns}
                      onChange={(_, newValue) =>
                        formikForm.setFieldValue(
                          "hidden_columns",
                          newValue.map((opt) => opt.value)
                        )
                      }
                      getOptionLabel={(option) => option?.label || ""}
                      isOptionEqualToValue={(option, value) =>
                        option.value === value.value
                      }
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            key={option.value}
                            label={option.label}
                            {...getTagProps({ index })}
                            size="medium"
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <CustomInputField
                          {...params}
                          className="hidden-columns-autocomplete"
                          name="hidden_columns"
                          placeholder="Select columns"
                          sx={{
                            "& > .MuiFilledInput-root": {
                              borderRadius: "8px"
                            }
                          }}
                        />
                      )}
                      sx={{
                        "& > .MuiInputBase-root.MuiFilledInput-root": {
                          paddingTop: "0px !important",
                        }
                      }}
                    />
                  </Box>
                </Grid>
                <Box sx={{
                  display: "flex", justifyContent: { xs: "center", md: "flex-end" }, width: "100%",
                }}>
                  <Primary
                    type="submit"
                    disabled={formikForm.isSubmitting && isUpdating}
                    sx={{
                      padding: "0.75rem 1.25rem", fontSize: "1rem",
                      minWidth: "150px", marginTop: "1.5rem",
                    }}
                  >
                    {isUpdating && formikForm.isSubmitting ? <CircularProgress sx={{ color: "white", marginInline: 'auto' }} size={'1.75rem'} /> : "Save"}
                  </Primary>
                </Box>
              </Grid>
            </Form>
          </FormikProvider>
        </Box>
        )}
      </PerfectScrollbar>
    </Box>
  );
};

export default DealFlow;


